import { useState, useEffect, useCallback } from "react";

function diffArtifacts(prodList, draftList, nameKey = "displayName") {
  const prodMap = Object.fromEntries(prodList.map(i => [i[nameKey], i]));
  const draftMap = Object.fromEntries(draftList.map(i => [i[nameKey], i]));
  const allNames = [...new Set([...Object.keys(prodMap), ...Object.keys(draftMap)])];
  return allNames.map(name => {
    const prod = prodMap[name] || null;
    const draft = draftMap[name] || null;
    let status, diffKeys = [];
    if (prod && draft) {
      const allKeys = [...new Set([...Object.keys(prod), ...Object.keys(draft)])];
      diffKeys = allKeys.filter(k => JSON.stringify(prod[k]) !== JSON.stringify(draft[k]));
      status = diffKeys.length === 0 ? "match" : "mismatch";
    } else if (prod) { status = "prod_only"; }
    else { status = "draft_only"; }
    return { name, prod, draft, status, diffKeys };
  }).sort((a, b) => {
    const order = { mismatch: 0, prod_only: 1, draft_only: 2, match: 3 };
    const sd = order[a.status] - order[b.status];
    return sd !== 0 ? sd : a.name.localeCompare(b.name);
  });
}

// User group capability diff
function getDimId(df) {
  if (df.staticDimensionFilter)  return df.staticDimensionFilter.dimensionId;
  if (df.dynamicDimensionFilter) return df.dynamicDimensionFilter.dimensionId;
  return JSON.stringify(df);
}

function flattenDimFilters(dataSecurityProfile) {
  const map = {};
  if (!dataSecurityProfile?.memberFilterConfigs) return map;
  for (const mfc of dataSecurityProfile.memberFilterConfigs) {
    for (const df of mfc.dimensionFilters || []) {
      const id = getDimId(df);
      map[id] = df;
    }
  }
  return map;
}

function getMemberSummary(df) {
  if (!df) return "-";
  const filter = df.staticDimensionFilter || df.dynamicDimensionFilter;
  if (!filter) return "-";
  if (filter.memberSelections) {
    const selections = filter.memberSelections.filter(m => m.namePath);
    const paths = selections.slice(0, 5).map(m => `${m.excluded ? "excl: " : ""}${m.namePath.join("/")}`);
    const extra = selections.length > 5 ? ` +${selections.length - 5} more` : "";
    return paths.length > 0 ? paths.join(", ") + extra : "all members";
  }
  if (filter.dynamicPropertyMappings) return filter.dynamicPropertyMappings.map(m => m.hierarchyPropertyId).join(", ");
  return "configured";
}

function diffPermissions(prodList, draftList) {
  const prodMap = Object.fromEntries(prodList.map(i => [i.displayName, i]));
  const draftMap = Object.fromEntries(draftList.map(i => [i.displayName, i]));
  const allNames = [...new Set([...Object.keys(prodMap), ...Object.keys(draftMap)])];
  return allNames.map(name => {
    const prod = prodMap[name] || null;
    const draft = draftMap[name] || null;
    let status = "match", profileDiffs = [];
    if (prod && draft) {
      const capChanged = JSON.stringify(prod.adminCapabilityConfig || {}) !== JSON.stringify(draft.adminCapabilityConfig || {});
      const prodProfiles = prod.dataSecurityProfiles || [];
      const draftProfiles = draft.dataSecurityProfiles || [];
      const prodPMap = Object.fromEntries(prodProfiles.map(p => [p.analyticObjectId, p]));
      const draftPMap = Object.fromEntries(draftProfiles.map(p => [p.analyticObjectId, p]));
      const allObjIds = [...new Set([...Object.keys(prodPMap), ...Object.keys(draftPMap)])];
      profileDiffs = allObjIds.map(objId => {
        const pp = prodPMap[objId] || null;
        const dp = draftPMap[objId] || null;
        let pStatus = "match", dimDiffs = [];
        if (pp && dp) {
          const prodDims = flattenDimFilters(pp);
          const draftDims = flattenDimFilters(dp);
          const allDimIds = [...new Set([...Object.keys(prodDims), ...Object.keys(draftDims)])];
          dimDiffs = allDimIds.map(dimId => {
            const pd = prodDims[dimId] || null;
            const dd = draftDims[dimId] || null;
            let dStatus = "match";
            if (pd && !dd) dStatus = "prod_only";
            else if (!pd && dd) dStatus = "draft_only";
            else if (JSON.stringify(pd) !== JSON.stringify(dd)) dStatus = "mismatch";
            return { dimId, prod: pd, draft: dd, status: dStatus };
          }).sort((a, b) => {
            const o = { mismatch: 0, prod_only: 1, draft_only: 2, match: 3 };
            return (o[a.status] - o[b.status]) || a.dimId.localeCompare(b.dimId);
          });
          pStatus = dimDiffs.some(d => d.status !== "match") ? "mismatch" : "match";
        } else if (pp) {
          pStatus = "prod_only";
          const dims = flattenDimFilters(pp);
          dimDiffs = Object.entries(dims).map(([dimId, df]) => ({ dimId, prod: df, draft: null, status: "prod_only" }));
        } else {
          pStatus = "draft_only";
          const dims = flattenDimFilters(dp);
          dimDiffs = Object.entries(dims).map(([dimId, df]) => ({ dimId, prod: null, draft: df, status: "draft_only" }));
        }
        return { objId, prod: pp, draft: dp, status: pStatus, dimDiffs };
      });
      status = (profileDiffs.some(p => p.status !== "match") || capChanged) ? "mismatch" : "match";
    } else if (prod) {
      status = "prod_only";
      profileDiffs = (prod.dataSecurityProfiles || []).map(p => {
        const dims = flattenDimFilters(p);
        return { objId: p.analyticObjectId, prod: p, draft: null, status: "prod_only", dimDiffs: Object.entries(dims).map(([dimId, df]) => ({ dimId, prod: df, draft: null, status: "prod_only" })) };
      });
    } else {
      status = "draft_only";
      profileDiffs = (draft.dataSecurityProfiles || []).map(p => {
        const dims = flattenDimFilters(p);
        return { objId: p.analyticObjectId, prod: null, draft: p, status: "draft_only", dimDiffs: Object.entries(dims).map(([dimId, df]) => ({ dimId, prod: null, draft: df, status: "draft_only" })) };
      });
    }
    const diffCount = profileDiffs.filter(p => p.status !== "match").length;
    return { name, prod, draft, status, profileDiffs, diffCount };
  }).sort((a, b) => {
    const order = { mismatch: 0, prod_only: 1, draft_only: 2, match: 3 };
    const sd = order[a.status] - order[b.status];
    return sd !== 0 ? sd : a.name.localeCompare(b.name);
  });
}

// Content Package diff
function diffContentPackages(prodList, draftList) {
  const prodMap = Object.fromEntries(prodList.map(i => [i.displayName, i]));
  const draftMap = Object.fromEntries(draftList.map(i => [i.displayName, i]));
  const allNames = [...new Set([...Object.keys(prodMap), ...Object.keys(draftMap)])];
  const shouldCompareArtifact = (artifactType, artifact) => {
    if (artifactType === "keyGroup") return artifact.canFilterBy === true || artifact.canGroupBy === true;
    if (artifactType === "guidebookTopic") return true;
    return artifact.visible === true;
  };
  
  return allNames.map(name => {
    const prod = prodMap[name] || null;
    const draft = draftMap[name] || null;
    let status = "match", artifactTypeDiffs = [];
    
    if (prod && draft) {
      // Compare artifactsByType
      const prodTypes = prod.artifactsByType || [];
      const draftTypes = draft.artifactsByType || [];
      const prodTypeMap = Object.fromEntries(prodTypes.map(t => [t.artifactType, t.artifacts || []]));
      const draftTypeMap = Object.fromEntries(draftTypes.map(t => [t.artifactType, t.artifacts || []]));
      const allTypes = [...new Set([...Object.keys(prodTypeMap), ...Object.keys(draftTypeMap)])];
      
      artifactTypeDiffs = allTypes.map(artifactType => {
        const prodArtifacts = prodTypeMap[artifactType] || [];
        const draftArtifacts = draftTypeMap[artifactType] || [];
        
        const visibleProdArtifacts = prodArtifacts.filter(artifact => shouldCompareArtifact(artifactType, artifact));
        const visibleDraftArtifacts = draftArtifacts.filter(artifact => shouldCompareArtifact(artifactType, artifact));
        
        // Create lookup by objectName or uuid or displayName
        const getArtifactKey = (a) => a.objectName || a.uuid || a.displayName || JSON.stringify(a);
        const prodKeys = visibleProdArtifacts.map(getArtifactKey);
        const draftKeys = visibleDraftArtifacts.map(getArtifactKey);
        const allKeys = [...new Set([...prodKeys, ...draftKeys])];
        
        const artifactDiffs = allKeys.map(key => {
          const pa = prodKeys.indexOf(key) >= 0 ? visibleProdArtifacts[prodKeys.indexOf(key)] : null;
          const da = draftKeys.indexOf(key) >= 0 ? visibleDraftArtifacts[draftKeys.indexOf(key)] : null;
          let aStatus = "match";
          
          if (pa && !da) {
            aStatus = "prod_only";
          } else if (!pa && da) {
            aStatus = "draft_only";
          } else if (pa && da && artifactType !== "guidebookTopic") {
            // For keyGroups: compare canFilterBy and canGroupBy
            // For other types: compare ONLY visibility (not names)
            let prodProps, draftProps;
            
            if (artifactType === "keyGroup") {
              prodProps = {
                canFilterBy: pa.canFilterBy,
                canGroupBy: pa.canGroupBy,
              };
              draftProps = {
                canFilterBy: da.canFilterBy,
                canGroupBy: da.canGroupBy,
              };
            } else {
              // For analysis, metrics, dimensions, guidebookTopics, reports
              // Only compare visible property - name differences don't matter
              prodProps = {
                visible: pa.visible,
              };
              draftProps = {
                visible: da.visible,
              };
            }
            
            if (JSON.stringify(prodProps) !== JSON.stringify(draftProps)) {
              aStatus = "mismatch";
            }
          }
          return { key, prod: pa, draft: da, status: aStatus };
        }).filter(a => a.status !== "match") // Only show differences
        .sort((a, b) => {
          const o = { mismatch: 0, prod_only: 1, draft_only: 2 };
          return (o[a.status] - o[b.status]) || a.key.localeCompare(b.key);
        });
        
        const typeStatus = artifactDiffs.length > 0 ? "mismatch" : "match";
        return { artifactType, artifactDiffs, status: typeStatus };
      }).filter(t => t.status !== "match") // Only show artifact types with differences
      .sort((a, b) => {
        const o = { mismatch: 0, prod_only: 1, draft_only: 2 };
        return (o[a.status] - o[b.status]) || a.artifactType.localeCompare(b.artifactType);
      });
      
      status = artifactTypeDiffs.length > 0 ? "mismatch" : "match";
    } else if (prod) {
      status = "prod_only";
      artifactTypeDiffs = (prod.artifactsByType || []).map(t => {
        const artifacts = t.artifacts || [];
        const visibleArtifacts = artifacts.filter(artifact => shouldCompareArtifact(t.artifactType, artifact));
        return {
          artifactType: t.artifactType,
          artifactDiffs: visibleArtifacts.map(a => ({
            key: a.objectName || a.uuid || a.displayName || JSON.stringify(a),
            prod: a,
            draft: null,
            status: "prod_only"
          })),
          status: "prod_only"
        };
      }).filter(t => t.artifactDiffs.length > 0);
    } else {
      status = "draft_only";
      artifactTypeDiffs = (draft.artifactsByType || []).map(t => {
        const artifacts = t.artifacts || [];
        const visibleArtifacts = artifacts.filter(artifact => shouldCompareArtifact(t.artifactType, artifact));
        return {
          artifactType: t.artifactType,
          artifactDiffs: visibleArtifacts.map(a => ({
          key: a.objectName || a.uuid || a.displayName || JSON.stringify(a),
          prod: null,
          draft: a,
          status: "draft_only"
        })),
        status: "draft_only"
        };
      }).filter(t => t.artifactDiffs.length > 0);
    }
    
    const diffCount = artifactTypeDiffs.length;
    return { name, prod, draft, status, artifactTypeDiffs, diffCount };
  }).sort((a, b) => {
    const order = { mismatch: 0, prod_only: 1, draft_only: 2, match: 3 };
    const sd = order[a.status] - order[b.status];
    return sd !== 0 ? sd : a.name.localeCompare(b.name);
  });
}

function diffUserGroups(prodList, draftList) {
  const prodMap = Object.fromEntries((prodList || []).map(i => [i.displayName, i]));
  const draftMap = Object.fromEntries((draftList || []).map(i => [i.displayName, i]));
  const allNames = [...new Set([...Object.keys(prodMap), ...Object.keys(draftMap)])];

  return allNames.map((name) => {
    const prod = prodMap[name] || null;
    const draft = draftMap[name] || null;

    if (prod && draft) {
      const prodCaps = new Set(prod.capabilities || []);
      const draftCaps = new Set(draft.capabilities || []);
      const allCaps = [...new Set([...(prod.capabilities || []), ...(draft.capabilities || [])])].sort();

      const capDiffs = allCaps.map((cap) => {
        const inProd = prodCaps.has(cap);
        const inDraft = draftCaps.has(cap);
        return {
          cap,
          inProd,
          inDraft,
          status: inProd && inDraft ? "match" : inProd ? "prod_only" : "draft_only",
        };
      });

      const changedCount = capDiffs.filter(c => c.status !== "match").length;
      return {
        name,
        prod,
        draft,
        status: changedCount > 0 ? "mismatch" : "match",
        capDiffs,
        changedCount,
      };
    }

    if (prod) {
      return {
        name,
        prod,
        draft: null,
        status: "prod_only",
        capDiffs: (prod.capabilities || []).map(cap => ({ cap, inProd: true, inDraft: false, status: "prod_only" })),
        changedCount: (prod.capabilities || []).length,
      };
    }

    return {
      name,
      prod: null,
      draft,
      status: "draft_only",
      capDiffs: (draft?.capabilities || []).map(cap => ({ cap, inProd: false, inDraft: true, status: "draft_only" })),
      changedCount: (draft?.capabilities || []).length,
    };
  }).sort((a, b) => {
    const order = { mismatch: 0, prod_only: 1, draft_only: 2, match: 3 };
    const sd = order[a.status] - order[b.status];
    return sd !== 0 ? sd : a.name.localeCompare(b.name);
  });
}

// Shared components
function StatusBadge({ status }) {
  const map = {
    match:      { label: "Match",      cls: "badge-match" },
    mismatch:   { label: "Mismatch",   cls: "badge-mismatch" },
    prod_only:  { label: "Prod only",  cls: "badge-prod" },
    draft_only: { label: "Draft only", cls: "badge-draft" },
  };
  const { label, cls } = map[status] || {};
  return <span className={`badge ${cls}`}>{label}</span>;
}

function StatCard({ value, label, color }) {
  return (
    <div className="stat-card">
      <div className="stat-value" style={{ color }}>{value}</div>
      <div className="stat-label">{label}</div>
    </div>
  );
}

// User Group row
function UserGroupRow({ row }) {
  const [expanded, setExpanded] = useState(false);
  const canExpand = row.status !== "match";
  const visibleCapDiffs = row.capDiffs.filter(c => c.status !== "match");

  return (
    <>
      <div
        className={`diff-row diff-row-${row.status} ${canExpand ? "expandable" : ""}`}
        onClick={() => canExpand && setExpanded(e => !e)}
      >
        <div className="col-name">
          <span className="artifact-name">{row.name}</span>
          {row.status === "mismatch" && row.changedCount > 0 && (
            <span className="diff-count-badge">{row.changedCount} capability{row.changedCount !== 1 ? "s" : ""} changed</span>
          )}
          {canExpand && <span className="expand-hint">{expanded ? "- collapse" : "+ expand"}</span>}
        </div>
        <div className="col-prod">
          {row.prod
            ? <span className="cell-present">{(row.prod.capabilities || []).length} capabilities</span>
            : <span className="cell-missing">- missing -</span>}
        </div>
        <div className="col-draft">
          {row.draft
            ? <span className="cell-present">{(row.draft.capabilities || []).length} capabilities</span>
            : <span className="cell-missing">- missing -</span>}
        </div>
        <div className="col-status"><StatusBadge status={row.status} /></div>
      </div>
      {expanded && (
        <div className="diff-expand">
          <div className="cap-table">
            <div className="cap-header">
              <div className="cap-col cap-col-name">Capability</div>
              <div className="cap-col cap-col-prod">Production</div>
              <div className="cap-col cap-col-draft">Draft</div>
              <div className="cap-col cap-col-status">Status</div>
            </div>
            {visibleCapDiffs.map(c => (
              <div key={c.cap} className={`cap-row cap-row-${c.status}`}>
                <div className="cap-col cap-col-name"><span className="cap-name">{c.cap}</span></div>
                <div className="cap-col cap-col-prod">
                  {c.inProd ? <span className="cap-yes">Yes</span> : <span className="cap-no">No</span>}
                </div>
                <div className="cap-col cap-col-draft">
                  {c.inDraft ? <span className="cap-yes">Yes</span> : <span className="cap-no">No</span>}
                </div>
                <div className="cap-col cap-col-status"><StatusBadge status={c.status} /></div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

function UserGroupsSection({ rows }) {
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("changed");
  const counts = {
    total: rows.length,
    match: rows.filter(r => r.status === "match").length,
    mismatch: rows.filter(r => r.status === "mismatch").length,
    prodOnly: rows.filter(r => r.status === "prod_only").length,
    draftOnly: rows.filter(r => r.status === "draft_only").length,
  };
  const filtered = rows.filter(r => {
    const matchSearch = r.name.toLowerCase().includes(search.toLowerCase());
    const matchFilter =
      filterStatus === "all"
      || (filterStatus === "changed" ? r.status !== "match" : r.status === filterStatus);
    return matchSearch && matchFilter;
  });
  return (
    <div className="artifact-section">
      <div className="section-header">
        <span className="section-icon">UG</span>
        <h2>User Groups</h2>
      </div>
      <div className="stats-row">
        <StatCard value={counts.total}     label="Total"      color="var(--text-primary)" />
        <StatCard value={counts.match}     label="Matching"   color="var(--green)" />
        <StatCard value={counts.mismatch}  label="Mismatch"   color="var(--amber)" />
        <StatCard value={counts.prodOnly}  label="Prod only"  color="var(--blue)" />
        <StatCard value={counts.draftOnly} label="Draft only" color="var(--teal)" />
      </div>
      <div className="filter-row">
        <input className="search-input" type="text" placeholder="Search by group name..." value={search} onChange={e => setSearch(e.target.value)} />
        <select className="filter-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="changed">Changed only ({counts.total - counts.match})</option>
          <option value="all">All ({counts.total})</option>
          <option value="mismatch">Mismatch ({counts.mismatch})</option>
          <option value="prod_only">Prod only ({counts.prodOnly})</option>
          <option value="draft_only">Draft only ({counts.draftOnly})</option>
          <option value="match">Match ({counts.match})</option>
        </select>
      </div>
      <div className="diff-table">
        <div className="diff-header">
          <div className="col-name">Group name</div>
          <div className="col-prod">Production</div>
          <div className="col-draft">Draft</div>
          <div className="col-status">Status</div>
        </div>
        {filtered.length === 0
          ? <div className="empty-state">No groups match the current filters.</div>
          : filtered.map(row => <UserGroupRow key={row.name} row={row} />)}
      </div>
    </div>
  );
}

// Permission components
function DimTable({ dimDiffs }) {
  const visibleDimDiffs = dimDiffs.filter(dim => dim.status !== "match");
  if (visibleDimDiffs.length === 0) {
    return <div className="perm-profile-note">No differences found for dimension filters.</div>;
  }

  return (
    <div className="dim-table">
      <div className="dim-header">
        <div className="dim-col dim-col-id">Dimension filter</div>
        <div className="dim-col dim-col-prod">Production</div>
        <div className="dim-col dim-col-draft">Draft</div>
        <div className="dim-col dim-col-status">Status</div>
      </div>
      {visibleDimDiffs.map(dim => (
        <div key={dim.dimId} className={`dim-row dim-row-${dim.status}`}>
          <div className="dim-col dim-col-id"><span className="dim-id">{dim.dimId.replace("Employee.", "")}</span></div>
          <div className="dim-col dim-col-prod">{dim.prod ? <span className="dim-val">{getMemberSummary(dim.prod)}</span> : <span className="cell-missing">- missing -</span>}</div>
          <div className="dim-col dim-col-draft">{dim.draft ? <span className="dim-val">{getMemberSummary(dim.draft)}</span> : <span className="cell-missing">- missing -</span>}</div>
          <div className="dim-col dim-col-status"><StatusBadge status={dim.status} /></div>
        </div>
      ))}
    </div>
  );
}

// Content Package components
function getArtifactSummary(artifact, artifactType) {
  if (!artifact) return null;
  
  // For keyGroups: show canFilterBy and canGroupBy
  // For other types: show visible
  if (artifactType === "keyGroup") {
    const parts = [];
    if (artifact.canFilterBy !== undefined) parts.push(`Filter: ${artifact.canFilterBy ? 'Yes' : 'No'}`);
    if (artifact.canGroupBy !== undefined) parts.push(`Group: ${artifact.canGroupBy ? 'Yes' : 'No'}`);
    return parts.join(', ');
  } else {
    // For analysis, metrics, dimensions, reports, guidebookTopics
    if (artifact.visible !== undefined) {
      return artifact.visible ? 'Visible' : 'Not Visible';
    }
  }
  
  return 'Present';
}

function getGuidebookName(objectName) {
  const prefix = (objectName || "").split(".")[0];
  const suffix = "_Guidebook";
  if (!prefix.endsWith(suffix)) return null;
  return prefix.slice(0, -suffix.length).split("_").filter(Boolean).join(" ");
}

function ContentPackageArtifactTable({ artifactDiffs, artifactType }) {
  const visibleDiffs = artifactDiffs.filter(a => a.status !== "match");
  if (visibleDiffs.length === 0) {
    return <div className="perm-profile-note">No differences found for {artifactType} artifacts.</div>;
  }

  return (
    <div className="dim-table">
      <div className="dim-header">
        <div className="dim-col dim-col-id">Artifact</div>
        <div className="dim-col dim-col-prod">Production</div>
        <div className="dim-col dim-col-draft">Draft</div>
        <div className="dim-col dim-col-status">Status</div>
      </div>
      {visibleDiffs.map((artifact, idx) => {
        const prodSummary = getArtifactSummary(artifact.prod, artifactType);
        const draftSummary = getArtifactSummary(artifact.draft, artifactType);
        const guidebookName = artifactType === "guidebookTopic"
          ? getGuidebookName(artifact.draft?.objectName || artifact.prod?.objectName || artifact.key)
          : null;
        const isPropertyChange = artifact.status === "mismatch" && prodSummary !== draftSummary;
        
        return (
          <div key={idx} className={`dim-row dim-row-${artifact.status}`}>
            <div className={`dim-col dim-col-id${guidebookName ? " dim-col-guidebook" : ""}`}>
              <span className="dim-id">
                {artifact.prod?.displayName || artifact.draft?.displayName || artifact.key}
              </span>
              {guidebookName && <span className="dim-guide-label">Guide: {guidebookName}</span>}
            </div>
            <div className="dim-col dim-col-prod">
              {artifact.prod ? (
                <span className={`dim-val ${isPropertyChange ? 'text-warning' : ''}`}>
                  {prodSummary}
                </span>
              ) : (
                <span className="cell-missing">- missing -</span>
              )}
            </div>
            <div className="dim-col dim-col-draft">
              {artifact.draft ? (
                <span className={`dim-val ${isPropertyChange ? 'text-warning' : ''}`}>
                  {draftSummary}
                </span>
              ) : (
                <span className="cell-missing">- missing -</span>
              )}
            </div>
            <div className="dim-col dim-col-status">
              <StatusBadge status={artifact.status} />
              {isPropertyChange && <span className="diff-hint"> (properties changed)</span>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ContentPackageDetail({ row }) {
  const hasTypeDiffs = row.artifactTypeDiffs && row.artifactTypeDiffs.length > 0;

  return (
    <div className="diff-expand">
      {hasTypeDiffs && row.artifactTypeDiffs.map(typeInfo => (
        <div key={typeInfo.artifactType} className="perm-profile-block">
          <div className="perm-profile-header">
            <span className="perm-profile-title">{typeInfo.artifactType}</span>
            <StatusBadge status={typeInfo.status} />
          </div>
          {(typeInfo.status === "draft_only" || typeInfo.status === "prod_only") ? (
            <div className="profile-detail-summary">
              <div className="profile-summary-rows">
                <div className="profile-summary-row">
                  <span className="profile-summary-label">Change</span>
                  <span className={`profile-summary-val ${typeInfo.status === "draft_only" ? "val-draft" : "val-prod"}`}>
                    {typeInfo.status === "draft_only" ? "Added in draft" : "Removed in draft"}
                  </span>
                </div>
                <div className="profile-summary-row">
                  <span className="profile-summary-label">Count</span>
                  <span className="profile-summary-val">{typeInfo.artifactDiffs.length} artifacts</span>
                </div>
                {typeInfo.artifactDiffs.length > 0 && (
                  <ContentPackageArtifactTable artifactDiffs={typeInfo.artifactDiffs} artifactType={typeInfo.artifactType} />
                )}
              </div>
            </div>
          ) : (
            typeInfo.artifactDiffs.length > 0 ? (
              <ContentPackageArtifactTable artifactDiffs={typeInfo.artifactDiffs} artifactType={typeInfo.artifactType} />
            ) : (
              <div className="perm-profile-note">No artifacts configured</div>
            )
          )}
        </div>
      ))}
      {!hasTypeDiffs && (
        <div className="perm-profile-note">No detail available for this difference.</div>
      )}
    </div>
  );
}

function ContentPackageRow({ row }) {
  const [expanded, setExpanded] = useState(false);
  const canExpand = row.status !== "match";
  return (
    <>
      <div
        className={`diff-row diff-row-${row.status} ${canExpand ? "expandable" : ""}`}
        onClick={() => canExpand && setExpanded(e => !e)}
      >
        <div className="col-name">
          <span className="artifact-name">{row.name}</span>
          {row.status === "mismatch" && row.diffCount > 0 && <span className="diff-count-badge">{row.diffCount} artifact type{row.diffCount !== 1 ? "s" : ""} differ</span>}
          {canExpand && <span className="expand-hint">{expanded ? "- collapse" : "+ expand"}</span>}
        </div>
        <div className="col-prod">{row.prod ? <span className="cell-present">Present</span> : <span className="cell-missing">- missing -</span>}</div>
        <div className="col-draft">{row.draft ? <span className="cell-present">Present</span> : <span className="cell-missing">- missing -</span>}</div>
        <div className="col-status"><StatusBadge status={row.status} /></div>
      </div>
      {expanded && <ContentPackageDetail row={row} />}
    </>
  );
}

function PermissionDetail({ row, dasLookup = {} }) {
  // Check if capabilities differ
  const prodCaps = row.prod?.adminCapabilityConfig?.capabilities || [];
  const draftCaps = row.draft?.adminCapabilityConfig?.capabilities || [];
  const prodCapsSet = new Set(prodCaps);
  const draftCapsSet = new Set(draftCaps);
  const allCaps = [...new Set([...prodCaps, ...draftCaps])].sort();
  const capDiffs = allCaps.map(cap => ({
    cap,
    inProd: prodCapsSet.has(cap),
    inDraft: draftCapsSet.has(cap),
    status: prodCapsSet.has(cap) && !draftCapsSet.has(cap) ? "prod_only"
          : !prodCapsSet.has(cap) && draftCapsSet.has(cap) ? "draft_only"
          : "match"
  })).filter(c => c.status !== "match");

  const hasProfileDiffs = row.profileDiffs && row.profileDiffs.length > 0;
  const hasCapDiffs = capDiffs.length > 0;

  return (
    <div className="diff-expand">
      {/* Capability differences */}
      {hasCapDiffs && (
        <div className="perm-profile-block">
          <div className="perm-profile-header">
            <span className="perm-profile-title">Capabilities</span>
            <StatusBadge status="mismatch" />
          </div>
          <div className="cap-table">
            <div className="cap-header">
              <div className="cap-col cap-col-name">Capability</div>
              <div className="cap-col cap-col-prod">Production</div>
              <div className="cap-col cap-col-draft">Draft</div>
              <div className="cap-col cap-col-status">Status</div>
            </div>
            {capDiffs.map(c => (
              <div key={c.cap} className={`cap-row cap-row-${c.status}`}>
                <div className="cap-col cap-col-name"><span className="cap-name">{c.cap}</span></div>
                <div className="cap-col cap-col-prod">{c.inProd ? <span className="cap-yes">Yes</span> : <span className="cap-no">No</span>}</div>
                <div className="cap-col cap-col-draft">{c.inDraft ? <span className="cap-yes">Yes</span> : <span className="cap-no">No</span>}</div>
                <div className="cap-col cap-col-status"><StatusBadge status={c.status} /></div>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Data security profile differences */}
      {hasProfileDiffs && row.profileDiffs.map(profile => (
        <div key={profile.objId} className="perm-profile-block">
          <div className="perm-profile-header">
            <span className="perm-profile-title">{profile.objId}</span>
            <StatusBadge status={profile.status} />
          </div>
          {(profile.status === "draft_only" || profile.status === "prod_only") ? (
            <div className="profile-detail-summary">
              {(() => {
                const p = profile.prod || profile.draft;
                const side = profile.status === "draft_only" ? "Added in draft" : "Removed in draft";
                const dasId = p?.propertySetConfig?.shareableDataAccessSet?.dataAccessSetId;
                const allAccess = p?.allDataPointAccess;
                const hasFilters = p?.memberFilterConfigs?.length > 0;
                return (
                  <div className="profile-summary-rows">
                    <div className="profile-summary-row">
                      <span className="profile-summary-label">Change</span>
                      <span className={`profile-summary-val ${profile.status === "draft_only" ? "val-draft" : "val-prod"}`}>{side}</span>
                    </div>
                    {dasId && (
                      <div className="profile-summary-row">
                        <span className="profile-summary-label">Data Access Set</span>
                        <span className="profile-summary-val">{dasLookup[dasId] || dasId}</span>
                      </div>
                    )}
                    <div className="profile-summary-row">
                      <span className="profile-summary-label">Population Access</span>
                      <span className="profile-summary-val">{allAccess ? "All Access" : hasFilters ? "Filtered" : "No filters"}</span>
                    </div>
                    {hasFilters && profile.dimDiffs.length > 0 && <DimTable dimDiffs={profile.dimDiffs} />}
                  </div>
                );
              })()}
            </div>
          ) : (
            profile.dimDiffs.length > 0
              ? <DimTable dimDiffs={profile.dimDiffs} />
              : <div className="perm-profile-note">No dimension filters configured</div>
          )}
        </div>
      ))}
      {/* Nothing to show */}
      {!hasCapDiffs && !hasProfileDiffs && (
        <div className="perm-profile-note">No detail available for this difference.</div>
      )}
    </div>
  );
}

function PermissionRow({ row, dasLookup = {} }) {
  const [expanded, setExpanded] = useState(false);
  const canExpand = row.status !== "match";
  return (
    <>
      <div
        className={`diff-row diff-row-${row.status} ${canExpand ? "expandable" : ""}`}
        onClick={() => canExpand && setExpanded(e => !e)}
      >
        <div className="col-name">
          <span className="artifact-name">{row.name}</span>
          {row.status === "mismatch" && row.diffCount > 0 && <span className="diff-count-badge">{row.diffCount} profile{row.diffCount !== 1 ? "s" : ""} differ</span>}
          {canExpand && <span className="expand-hint">{expanded ? "- collapse" : "+ expand"}</span>}
        </div>
        <div className="col-prod">{row.prod ? <span className="cell-present">Present</span> : <span className="cell-missing">- missing -</span>}</div>
        <div className="col-draft">{row.draft ? <span className="cell-present">Present</span> : <span className="cell-missing">- missing -</span>}</div>
        <div className="col-status"><StatusBadge status={row.status} /></div>
      </div>
      {expanded && <PermissionDetail row={row} dasLookup={dasLookup} />}
    </>
  );
}

// DAS components
function getItemKey(item) {
  if (typeof item !== "object" || item === null) return JSON.stringify(item);
  for (const k of ["id", "permissionId", "propertyId", "dimensionId", "capabilityId", "analyticObjectId", "name", "displayName"]) {
    if (item[k] !== undefined) return `${k}:${item[k]}`;
  }
  return JSON.stringify(item);
}

function formatPropertyLabel(raw) {
  if (!raw) return "";
  const overrides = {
    Current_Corp_Entity_LevelDim: "Corporate Entity",
    Corp_Hierarchy_Security: "Corp Hierarchy Security",
  };
  if (overrides[raw]) return overrides[raw];

  let label = String(raw)
    .replace(/\.Group_/g, " ")
    .replace(/[._]/g, " ")
    .replace(/LevelDim\b/gi, "")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim();

  // Keep common acronym capitalization for readability.
  label = label
    .replace(/\bId\b/g, "ID")
    .replace(/\bEeoc\b/g, "EEOC")
    .replace(/\bFte\b/g, "FTE")
    .replace(/\bHr\b/g, "HR")
    .replace(/\bPi(i)?\b/g, "PII");

  return label;
}

function diffArrays(prodArr, draftArr) {
  const prodKeys = prodArr.map(getItemKey);
  const draftKeys = draftArr.map(getItemKey);
  const allKeys = [...new Set([...prodKeys, ...draftKeys])];
  return allKeys.map(key => {
    const prodItem = prodKeys.indexOf(key) >= 0 ? prodArr[prodKeys.indexOf(key)] : null;
    const draftItem = draftKeys.indexOf(key) >= 0 ? draftArr[draftKeys.indexOf(key)] : null;
    let status = "match";
    if (prodItem && !draftItem) status = "prod_only";
    else if (!prodItem && draftItem) status = "draft_only";
    else if (JSON.stringify(prodItem) !== JSON.stringify(draftItem)) status = "changed";
    return { key, prodItem, draftItem, status };
  });
}

function ArrayDiff({ prodArr, draftArr, fieldKey }) {
  const items = diffArrays(prodArr || [], draftArr || []);
  const visibleItems = items.filter(i => i.status !== "match");
  if (!visibleItems.length) return <div className="array-nodiff"><span className="array-nodiff-label">- identical ({prodArr.length} items)</span></div>;
  return (
    <div className="array-diff-wrap">
      <div className="array-diff-header">
        <div className="array-col array-col-item">Item</div>
        <div className="array-col array-col-prod">Production</div>
        <div className="array-col array-col-draft">Draft</div>
        <div className="array-col array-col-status">Status</div>
      </div>
      {visibleItems.map((item, i) => (
        <div key={i} className={`array-diff-row array-row-${item.status}`}>
          <div className="array-col array-col-item"><span className="array-item-key">{fieldKey === "propertyAccessConfigs" ? formatPropertyLabel(item.key.replace(/^[^:]+:/, "")) : item.key.replace(/^[^:]+:/, "")}</span></div>
          <div className="array-col array-col-prod">{item.prodItem ? <span className="array-present">Present</span> : <span className="array-missing">- missing -</span>}</div>
          <div className="array-col array-col-draft">{item.draftItem ? <span className="array-present">Present</span> : <span className="array-missing">- missing -</span>}</div>
          <div className="array-col array-col-status">
            {item.status === "match"      && <span className="abadge abadge-match">Match</span>}
            {item.status === "prod_only"  && <span className="abadge abadge-prod">Prod only</span>}
            {item.status === "draft_only" && <span className="abadge abadge-draft">Draft only</span>}
            {item.status === "changed"    && <span className="abadge abadge-changed">Changed</span>}
          </div>
        </div>
      ))}
    </div>
  );
}

function DasRow({ row }) {
  const [expanded, setExpanded] = useState(false);
  const canExpand = row.status !== "match";
  return (
    <>
      <div
        className={`diff-row diff-row-${row.status} ${canExpand ? "expandable" : ""}`}
        onClick={() => canExpand && setExpanded(e => !e)}
      >
        <div className="col-name">
          <span className="artifact-name">{row.name}</span>
          {row.status === "mismatch" && <span className="diff-count-badge">{row.diffKeys.length} field{row.diffKeys.length !== 1 ? "s" : ""} differ</span>}
          {canExpand && <span className="expand-hint">{expanded ? "- collapse" : "+ expand"}</span>}
        </div>
        <div className="col-prod">{row.prod ? <span className="cell-present">Present</span> : <span className="cell-missing">- missing -</span>}</div>
        <div className="col-draft">{row.draft ? <span className="cell-present">Present</span> : <span className="cell-missing">- missing -</span>}</div>
        <div className="col-status"><StatusBadge status={row.status} /></div>
      </div>
      {expanded && (
        <div className="diff-expand">
          {row.status === "mismatch" && row.diffKeys.map(key => {
            const prodVal = row.prod?.[key];
            const draftVal = row.draft?.[key];
            const isArray = Array.isArray(prodVal) || Array.isArray(draftVal);
            return (
              <div key={key} className="diff-field-block">
                <div className="diff-field-block-header">
                  <span className="diff-field-block-key">{key}</span>
                  <span className="diff-field-block-tag">changed</span>
                </div>
                {isArray
                  ? <ArrayDiff prodArr={prodVal || []} draftArr={draftVal || []} fieldKey={key} />
                  : (
                    <div className="field-vals">
                      <div className="field-val-col">
                        <div className="field-val-label field-val-label-prod">Production</div>
                        <pre className="field-val-pre field-val-pre-prod">{JSON.stringify(prodVal, null, 2)}</pre>
                      </div>
                      <div className="field-val-col">
                        <div className="field-val-label field-val-label-draft">Draft</div>
                        <pre className="field-val-pre field-val-pre-draft">{JSON.stringify(draftVal, null, 2)}</pre>
                      </div>
                    </div>
                  )}
              </div>
            );
          })}
          {(row.status === "prod_only" || row.status === "draft_only") &&
            Object.entries(row.prod || row.draft || {}).map(([key, value]) => {
              const isArray = Array.isArray(value);
              const prodVal = row.status === "prod_only" ? value : undefined;
              const draftVal = row.status === "draft_only" ? value : undefined;
              return (
                <div key={key} className="diff-field-block">
                  <div className="diff-field-block-header">
                    <span className="diff-field-block-key">{key}</span>
                    <span className="diff-field-block-tag">
                      {row.status === "prod_only" ? "prod only" : "draft only"}
                    </span>
                  </div>
                  {isArray
                    ? <ArrayDiff prodArr={prodVal || []} draftArr={draftVal || []} fieldKey={key} />
                    : (
                      <div className="field-vals">
                        <div className="field-val-col">
                          <div className="field-val-label field-val-label-prod">Production</div>
                          {prodVal !== undefined
                            ? <pre className="field-val-pre field-val-pre-prod">{JSON.stringify(prodVal, null, 2)}</pre>
                            : <div className="field-val-pre field-val-pre-prod"><span className="cell-missing">- missing -</span></div>}
                        </div>
                        <div className="field-val-col">
                          <div className="field-val-label field-val-label-draft">Draft</div>
                          {draftVal !== undefined
                            ? <pre className="field-val-pre field-val-pre-draft">{JSON.stringify(draftVal, null, 2)}</pre>
                            : <div className="field-val-pre field-val-pre-draft"><span className="cell-missing">- missing -</span></div>}
                        </div>
                      </div>
                    )}
                </div>
              );
            })}
        </div>
      )}
    </>
  );
}

function DiffTable({ rows, search, filterStatus, type, dasLookup = {} }) {
  const filtered = rows.filter(r => {
    const matchSearch = r.name.toLowerCase().includes(search.toLowerCase());
    const matchFilter =
      filterStatus === "all"
      || (filterStatus === "changed" ? r.status !== "match" : r.status === filterStatus);
    return matchSearch && matchFilter;
  });
  if (!filtered.length) return <div className="empty-state">No artifacts match the current filters.</div>;
  return (
    <div className="diff-table">
      <div className="diff-header">
        <div className="col-name">Artifact name</div>
        <div className="col-prod">Production</div>
        <div className="col-draft">Draft</div>
        <div className="col-status">Status</div>
      </div>
      {filtered.map(row =>
        type === "permissions"
          ? <PermissionRow key={row.name} row={row} dasLookup={dasLookup} />
          : type === "contentPackages"
          ? <ContentPackageRow key={row.name} row={row} />
          : <DasRow key={row.name} row={row} />
      )}
    </div>
  );
}

function ArtifactSection({ title, rows, icon, type, dasLookup = {} }) {
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("changed");
  const counts = {
    total: rows.length,
    match: rows.filter(r => r.status === "match").length,
    mismatch: rows.filter(r => r.status === "mismatch").length,
    prodOnly: rows.filter(r => r.status === "prod_only").length,
    draftOnly: rows.filter(r => r.status === "draft_only").length,
  };
  return (
    <div className="artifact-section">
      <div className="section-header">
        <span className="section-icon">{icon}</span>
        <h2>{title}</h2>
      </div>
      <div className="stats-row">
        <StatCard value={counts.total}     label="Total"      color="var(--text-primary)" />
        <StatCard value={counts.match}     label="Matching"   color="var(--green)" />
        <StatCard value={counts.mismatch}  label="Mismatch"   color="var(--amber)" />
        <StatCard value={counts.prodOnly}  label="Prod only"  color="var(--blue)" />
        <StatCard value={counts.draftOnly} label="Draft only" color="var(--teal)" />
      </div>
      <div className="filter-row">
        <input className="search-input" type="text" placeholder="Search by name..." value={search} onChange={e => setSearch(e.target.value)} />
        <select className="filter-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="changed">Changed only ({counts.total - counts.match})</option>
          <option value="all">All ({counts.total})</option>
          <option value="mismatch">Mismatch ({counts.mismatch})</option>
          <option value="prod_only">Prod only ({counts.prodOnly})</option>
          <option value="draft_only">Draft only ({counts.draftOnly})</option>
          <option value="match">Match ({counts.match})</option>
        </select>
      </div>
      <DiffTable rows={rows} search={search} filterStatus={filterStatus} type={type} dasLookup={dasLookup} />
    </div>
  );
}

async function fetchPermissionsExpanded(apiFetch, projectId) {
  const response = await apiFetch(`/artifacts/permissions?projectId=${projectId}`);
  return {
    permissions: response.permissions || [],
    errorCount: Number(response.errorCount || 0),
  };
}

export default function PermissionComparator({ apiFetch }) {
  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [projectsError, setProjectsError] = useState(null);
  const [selectedProject, setSelectedProject] = useState("");
  const [comparing, setComparing] = useState(false);
  const [compareError, setCompareError] = useState(null);
  const [compareWarning, setCompareWarning] = useState(null);
  const [dasRows, setDasRows] = useState(null);
  const [psRows, setPsRows] = useState(null);
  const [ugRows, setUgRows] = useState(null);
  const [cpRows, setCpRows] = useState(null);
  const [dasLookup, setDasLookup] = useState({});
  const [dasLoading, setDasLoading] = useState(false);
  const [psLoading, setPsLoading] = useState(false);
  const [ugLoading, setUgLoading] = useState(false);
  const [cpLoading, setCpLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("das");

  useEffect(() => {
    apiFetch("/projects")
      .then(data => { setProjects(data.projects || []); setProjectsLoading(false); })
      .catch(err => { setProjectsError(err.message); setProjectsLoading(false); });
  }, []);

  const handleCompare = useCallback(async () => {
    if (!selectedProject) return;
    setCompareError(null);
    setCompareWarning(null);
    setDasRows(null); setPsRows(null); setUgRows(null); setCpRows(null);
    setDasLoading(true); setPsLoading(false); setUgLoading(false); setCpLoading(false);
    setComparing(true);
    setActiveTab("das");

    try {
      // Step 1 - Data Access Sets (show immediately when done)
      const prodDas  = await apiFetch("/artifacts/data-access-sets?projectId=production");
      const draftDas = await apiFetch(`/artifacts/data-access-sets?projectId=${selectedProject}`);
      // Build DAS ID -> displayName lookup for permission detail view
      const lookup = {};
      [...(prodDas.dataAccessSets || []), ...(draftDas.dataAccessSets || [])].forEach(d => {
        if (d.id) lookup[d.id] = d.displayName || d.id;
      });
      setDasLookup(lookup);
      setDasRows(diffArtifacts(prodDas.dataAccessSets || [], draftDas.dataAccessSets || [], "displayName"));
      setDasLoading(false);
      setPsLoading(true);

      // Step 2 - Permission Sets (expanded list requests reduce per-item API calls)
      const prodPs  = await fetchPermissionsExpanded(apiFetch, "production");
      const draftPs = await fetchPermissionsExpanded(apiFetch, selectedProject);
      const prodPsErrors = Number(prodPs?.errorCount || 0);
      const draftPsErrors = Number(draftPs?.errorCount || 0);
      if (prodPsErrors > 0 || draftPsErrors > 0) {
        setCompareWarning(
          `Permission set details were partially loaded (prod errors: ${prodPsErrors}, draft errors: ${draftPsErrors}). Results are shown with available data.`
        );
      }
      setPsRows(diffPermissions(prodPs.permissions || [], draftPs.permissions || []));
      setPsLoading(false);
      setUgLoading(true);

      // Step 3 - User Groups: fetch separately for prod and draft
      // Build capability map from prod permissions
      const prodCapMap = {};
      (prodPs.permissions || []).forEach(p => {
        prodCapMap[p.permissionId] = p.adminCapabilityConfig?.capabilities || [];
      });
      // Build capability map from draft permissions
      const draftCapMap = {};
      (draftPs.permissions || []).forEach(p => {
        draftCapMap[p.permissionId] = p.adminCapabilityConfig?.capabilities || [];
      });
      // Fetch user groups separately for prod and draft so membership differences are detected
      const ugProdData  = await apiFetch("/user-groups?projectId=production");
      const ugDraftData = await apiFetch(`/user-groups?projectId=${selectedProject}`);
      // Build prod user group capability lists using prod permission cap map
      const prodUgList = (ugProdData.userGroups || []).map(g => ({
        userGroupId: g.userGroupId,
        displayName: g.displayName,
        capabilities: [...new Set(
          (g.permissions || []).flatMap(p => prodCapMap[p.permissionId] || [])
        )].sort(),
      }));
      // Build draft user group capability lists using draft permission cap map
      const draftUgList = (ugDraftData.userGroups || []).map(g => ({
        userGroupId: g.userGroupId,
        displayName: g.displayName,
        capabilities: [...new Set(
          (g.permissions || []).flatMap(p => draftCapMap[p.permissionId] || prodCapMap[p.permissionId] || [])
        )].sort(),
      }));
      setUgRows(diffUserGroups(prodUgList, draftUgList));
      setUgLoading(false);
      setCpLoading(true);

      // Step 4 - Content Packages
      const prodCp  = await apiFetch("/artifacts/content-packages?projectId=production");
      const draftCp = await apiFetch(`/artifacts/content-packages?projectId=${selectedProject}`);
      setCpRows(diffContentPackages(prodCp.contentPackages || [], draftCp.contentPackages || []));
      setCpLoading(false);
    } catch (err) {
      setCompareError(err.message);
      setDasLoading(false); setPsLoading(false); setUgLoading(false); setCpLoading(false);
    } finally {
      setComparing(false);
    }
  }, [selectedProject]);

  const hasAnyResults = dasRows !== null || psRows !== null || ugRows !== null || cpRows !== null;
  const totalMismatches = [
    ...(dasRows || []),
    ...(psRows || []),
    ...(ugRows || []),
    ...(cpRows || []),
  ].filter(r => r.status && r.status !== "match").length;

  return (
    <div className="app">
      <header className="cug-header">
        <div className="cug-header-top">
          <div className="cug-title">Permission Comparator</div>
        </div>
        <div className="cug-subtitle">Production vs Draft</div>
      </header>

      <div className="control-bar">
        <div className="control-inner">
          <div className="control-group">
            <label className="control-label">Production</label>
            <div className="fixed-env prod-tag">Production (live)</div>
          </div>
          <div className="vs-divider">vs</div>
          <div className="control-group">
            <label className="control-label">Draft project</label>
            {projectsLoading ? <div className="loading-inline">Loading projects...</div>
              : projectsError ? <div className="error-inline">Error: {projectsError}</div>
              : (
                <select className="project-select" value={selectedProject} onChange={e => setSelectedProject(e.target.value)}>
                  <option value="">-- select a draft project --</option>
                  {projects.map(p => <option key={p.id} value={p.id}>{p.displayName || p.id}</option>)}
                </select>
              )}
          </div>
          <button className="compare-btn" disabled={!selectedProject || comparing} onClick={handleCompare}>
            {comparing ? <><span className="spinner" /> Comparing...</> : "Compare"}
          </button>
          {hasAnyResults && totalMismatches > 0 && (
            <div className="cug-nonversioned-badge">
              {totalMismatches} difference{totalMismatches !== 1 ? "s" : ""} found
            </div>
          )}
        </div>
      </div>

      <main className="main-content">
        {compareError && <div className="error-banner"><strong>Error:</strong> {compareError}</div>}
        {!compareError && compareWarning && <div className="error-banner"><strong>Warning:</strong> {compareWarning}</div>}

        {!hasAnyResults && !comparing && !compareError && (
          <div className="empty-hero">
            <div className="empty-icon">+</div>
            <div className="empty-title">Select a draft project to begin</div>
            <div className="empty-sub">Choose a draft project above and click Compare to see what's missing or different between production and your draft.</div>
          </div>
        )}

        {hasAnyResults && (
          <>
            <div className="tab-bar">
              <button className={`tab-btn ${activeTab === "das" ? "active" : ""}`} onClick={() => setActiveTab("das")}>
                Data Access Sets
                {dasLoading && <span className="tab-loading">loading...</span>}
                {dasRows && dasRows.filter(r => r.status !== "match").length > 0 && <span className="tab-badge">{dasRows.filter(r => r.status !== "match").length}</span>}
              </button>
              <button className={`tab-btn ${activeTab === "ps" ? "active" : ""}`} onClick={() => setActiveTab("ps")}>
                Permission Sets
                {psLoading && <span className="tab-loading">loading...</span>}
                {psRows && psRows.filter(r => r.status !== "match").length > 0 && <span className="tab-badge">{psRows.filter(r => r.status !== "match").length}</span>}
              </button>
              <button className={`tab-btn ${activeTab === "ug" ? "active" : ""}`} onClick={() => setActiveTab("ug")}>
                User Groups
                {ugLoading && <span className="tab-loading">loading...</span>}
                {ugRows && ugRows.filter(r => r.status !== "match").length > 0 && <span className="tab-badge">{ugRows.filter(r => r.status !== "match").length}</span>}
              </button>
              <button className={`tab-btn ${activeTab === "cp" ? "active" : ""}`} onClick={() => setActiveTab("cp")}>
                Content Packages
                {cpLoading && <span className="tab-loading">loading...</span>}
                {cpRows && cpRows.filter(r => r.status !== "match").length > 0 && <span className="tab-badge">{cpRows.filter(r => r.status !== "match").length}</span>}
              </button>
            </div>

            {activeTab === "das" && (
              dasRows
                ? <ArtifactSection title="Data Access Sets" rows={dasRows} icon="DAS" type="das" />
                : <div className="loading-state"><div className="loading-spinner" /><div>Loading Data Access Sets...</div></div>
            )}
            {activeTab === "ps" && (
              psRows
                ? <ArtifactSection title="Permission Sets" rows={psRows} icon="PS" type="permissions" dasLookup={dasLookup} />
                : <div className="loading-state"><div className="loading-spinner" /><div>Loading Permission Sets...</div></div>
            )}
            {activeTab === "ug" && (
              ugRows
                ? <UserGroupsSection rows={ugRows} />
                : <div className="loading-state"><div className="loading-spinner" /><div>Loading User Groups...</div></div>
            )}
            {activeTab === "cp" && (
              cpRows
                ? <ArtifactSection title="Content Packages" rows={cpRows} icon="CP" type="contentPackages" />
                : <div className="loading-state"><div className="loading-spinner" /><div>Loading Content Packages...</div></div>
            )}
          </>
        )}
      </main>
    </div>
  );
}


